import React, { useLayoutEffect, useRef, useState } from 'react';
import I18n from 'modules/i18n';
import { LinkButton } from '@/components/ui/Buttons';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';

interface StandardLabelProps {
  alias: string;
  description: string;
}

// +1 tolerance for -webkit-line-clamp rounding (scrollHeight ~1px > clientHeight on a perfect fit).
export const isClamped = (el: HTMLElement) => el.scrollHeight > el.clientHeight + 1;

// Standard code + description, clamped to two lines. A More/Less toggle sits at the right edge of
// the row only when the description actually overflows the clamp, and is revealed on row hover or
// when anything in the row (checkbox, chevron, the toggle itself) has keyboard focus — see
// `tw-group` on the row in StandardsTreeNode. :focus-visible, not :focus-within, so a mouse click on
// the toggle doesn't leave it pinned visible after the pointer moves on. Devices with no hover
// (touch) get it always visible.
const StandardLabel = ({ alias, description }: StandardLabelProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [clamped, setClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      if (textRef.current && !expanded) setClamped(isClamped(textRef.current));
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [description, expanded]);

  const showToggle = clamped || expanded;

  return (
    <span className="tw-flex tw-items-start tw-gap-2 tw-w-full tw-pl-1">
      <Text className="tw-block tw-flex-1 tw-min-w-0" size="sm" tag="span">
        <span ref={textRef} className={expanded ? 'tw-block' : 'tw-line-clamp-2'}>
          <span className="tw-font-bold">{alias}</span>
          {description && <span> {description}</span>}
        </span>
      </Text>
      {showToggle && (
        <LinkButton
          aria-expanded={expanded}
          aria-label={`${expanded ? I18n.t('less') : I18n.t('more')}: ${alias}`}
          className={cn(
            'tw-flex-shrink-0 tw-p-0 tw-text-sm tw-underline',
            'tw-opacity-0 group-hover:tw-opacity-100 group-has-[:focus-visible]:tw-opacity-100',
            '[@media(hover:none)]:tw-opacity-100',
          )}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? I18n.t('less') : I18n.t('more')}
        </LinkButton>
      )}
    </span>
  );
};

export default StandardLabel;
